import { useState, FormEvent, useRef, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Shield, Zap } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import { TextInputModern, Checkbox, Button, Badge } from '@pavanhiro/common-ui';

const API_BASE_URL = import.meta.env.VITE_AUTH_API_URL || 'https://zoauth-core-18974396984.us-central1.run.app';
const APP_NAME = import.meta.env.VITE_APP_NAME || 'ZoAuth';
const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'; // Test key
const IS_DEVELOPMENT = import.meta.env.DEV || import.meta.env.MODE === 'development';

// Dev-mode quick login personas (same as salon-admin-portal)
interface DevPersona {
  label: string;
  email: string;
  password: string;
  color: string;
  description: string;
}

const DEV_PERSONAS: DevPersona[] = [
  { label: 'Super Admin', email: 'superadmin@zoauth.com', password: 'Admin123!@Pwd', color: '#7c3aed', description: 'Full platform access' },
  { label: 'Admin', email: 'admin@demosalon.com', password: 'Admin123!@Pwd', color: '#2563eb', description: 'Organization administrator' },
  { label: 'Staff', email: 'staff@demosalon.com', password: 'Admin123!@Pwd', color: '#059669', description: 'Regular employee' },
  { label: 'Customer', email: 'customer@example.com', password: 'Admin123!@Pwd', color: '#f97316', description: 'End user / customer' },
] as const;

interface LoginForm {
  username: string;
  password: string;
}

// Shape returned by GET /api/v1/tenants/{tenantID}/idps
interface PublicIDP {
  id: string;
  type: 'google' | 'oidc' | 'saml' | 'ldap' | 'github';
  name: string;
  logo_hint: string;
}

// Map IDP type â†’ SVG icon path (inline, no external deps)
const IDP_ICONS: Record<string, string> = {
  google: 'M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.28 5 12c0-4.2 3.29-7.27 7.19-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81z',
  github: 'M12 2C6.48 2 2 6.48 2 12c0 4.42 2.87 8.17 6.84 9.49.5.09.66-.22.66-.48v-1.69c-2.78.6-3.37-1.34-3.37-1.34-.46-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.89 1.52 2.34 1.08 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.94 0-1.09.39-1.98 1.03-2.68-.1-.25-.45-1.27.1-2.64 0 0 .84-.27 2.75 1.02A9.56 9.56 0 0112 6.8c.85 0 1.71.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.37.2 2.39.1 2.64.64.7 1.03 1.59 1.03 2.68 0 3.84-2.34 4.68-4.57 4.93.36.31.68.92.68 1.85v2.74c0 .27.16.58.67.48A10 10 0 0022 12c0-5.52-4.48-10-10-10z',
  oidc: 'M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
  saml: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
  ldap: 'M4 6h16M4 12h16M4 18h16',
};

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState<LoginForm>({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);
  const [, setCaptchaToken] = useState<string | null>(null);
  const [devOpen, setDevOpen] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [idps, setIdps] = useState<PublicIDP[]>([]);
  const [ssoLoading, setSsoLoading] = useState<string | null>(null); // idp id being initiated
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  // Show inline error if Spring proxy redirected back to /login?error.
  // AUTHCODE_NATIVE flow never adds ?error= to /login â€” kept for Spring fallback compat.
  useEffect(() => {
    if (searchParams.get('error')) {
      setError('Invalid username or password. Please try again.');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tenantId =
    searchParams.get('tenant_id') ||
    localStorage.getItem('tenant_id') ||
    'demo-salon';

  // Fetch configured identity providers for this tenant so we can render SSO buttons.
  // GET /api/v1/tenants/{tenantID}/idps â†’ { idps: [{ id, type, name, logo_hint }] }
  useEffect(() => {
    if (!tenantId) return;
    fetch(`${API_BASE_URL}/api/v1/tenants/${encodeURIComponent(tenantId)}/idps`, {
      headers: { 'Cache-Control': 'no-store' },
    })
      .then(r => r.ok ? r.json() : null)
      .then((data: { idps?: PublicIDP[] } | null) => {
        if (data?.idps?.length) setIdps(data.idps);
      })
      .catch(() => { /* silently ignore â€” SSO is additive */ });
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // OAuth2 flow parameters from query string.
  // ar= is the signed AuthRequest bridge token issued by Go's GET /oauth2/authorize handler.
  // Present when AUTHCODE_NATIVE=true; absent when the Spring AS proxy is still active.
  const arToken = searchParams.get('ar');

  // Decode the app name from the ar token payload (base64url JSON, no crypto needed).
  // Falls back to undefined so the UI shows the generic "ZoAuth" brand.
  const arAppName: string | undefined = (() => {
    if (!arToken) return undefined;
    try {
      const parts = arToken.split('.');
      if (parts.length !== 3) return undefined;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      return typeof payload.app === 'string' && payload.app ? payload.app : undefined;
    } catch {
      return undefined;
    }
  })();

  // Initiate SSO for a specific IdP â€” navigates the browser directly to the IdP.
  const handleSSOLogin = useCallback((idp: PublicIDP) => {
    setSsoLoading(idp.id);
    const emailDomain = form.username.includes('@') ? form.username.split('@')[1] : tenantId;
    const params = new URLSearchParams({ domain: emailDomain });
    if (arToken) params.set('ar', arToken);
    // GET /api/v1/auth/sso/initiate?domain=<domain> â†’ 302 to external IdP
    window.location.href = `${API_BASE_URL}/api/v1/auth/sso/initiate?${params}`;
  }, [form.username, tenantId, arToken]);

  /** Read XSRF-TOKEN cookie set by Spring's CookieCsrfTokenRepository */
  const getCsrfToken = (): string =>
    document.cookie.split(';').map(c => c.trim().split('=')).find(([n]) => n === 'XSRF-TOKEN')?.[1] ?? '';

  /**
   * Legacy Spring form POST â€” used when ar= is absent (AUTHCODE_NATIVE=false or
   * non-authorization_code flows). Native form POST is intentional: fetch with
   * redirect:'follow' would exhaust the one-time authorization code before the
   * browser sees it. Spring follows: /perform-login â†’ /oauth2/authorize â†’ callback.
   */
  const submitToSpring = (username: string, password: string) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/perform-login';
    for (const [name, value] of [
      ['username', username],
      ['password', password],
      ['_csrf', getCsrfToken()],
    ] as [string, string][]) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    }
    document.body.appendChild(form);
    form.submit();
  };

  /**
   * Go-native authorization_code login flow (AUTHCODE_NATIVE=true).
   *
   * Steps:
   *   1. POST /api/v1/auth/login â†’ { access_token }   (Go issues user JWT)
   *   2. POST /oauth2/authorize/complete { ar, access_token } â†’ { redirect_to }
   *      (Go verifies signed ar-token + user JWT, stores one-time code, returns redirect_to)
   *   3. window.location.href = redirect_to
   *      (Browser navigates to redirect_uri?code=<code>&state=<state>)
   *
   * Why this beats the Spring form POST:
   *   â€¢ ~50ms cold-start vs 8-15s for Spring Cloud Run (Go = min=1 instance always warm)
   *   â€¢ No hidden DOM forms, no CSRF cookie round-trips
   *   â€¢ Full observability: each step emits structured logs with trace_id / request_id
   *   â€¢ Rollback: flip AUTHCODE_NATIVE=false to revert to Spring flow without redeploying UI
   */
  const submitToGo = async (username: string, password: string) => {
    if (!arToken) {
      // Safety guard: ar= should always be present when AUTHCODE_NATIVE=true.
      // If somehow absent, fall back to Spring to avoid blank error screen.
      submitToSpring(username, password);
      return;
    }

    // Step 1: Authenticate user â€” Go REST login endpoint.
    // Returns a short-lived access_token we hand straight to /oauth2/authorize/complete.
    const loginResp = await fetch(`${API_BASE_URL}/api/v1/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant-ID': tenantId,
      },
      body: JSON.stringify({ email: username, password }),
    });

    const loginData = await loginResp.json();

    if (!loginResp.ok) {
      // Go returns RFC 6749-style errors or {error, message} on auth failures.
      // On brute-force lockout: { error, error_description, attempts_remaining?, retry_after? }
      if (typeof loginData?.attempts_remaining === 'number') {
        setAttemptsRemaining(loginData.attempts_remaining);
      }
      const msg = loginData?.error_description ||
                  loginData?.message ||
                  'Invalid username or password.';
      throw new Error(msg);
    }
    // Clear lockout counter on successful auth
    setAttemptsRemaining(null);

    // MFA required: Go returns { mfa_required: true, session_id, methods }
    // instead of an access_token. Redirect to the MFA challenge page.
    if (loginData?.mfa_required === true) {
      navigate('/mfa-challenge', {
        state: {
          sessionId: loginData.session_id,
          methods: loginData.methods ?? ['totp'],
          userId: loginData.user_id,
          // Preserve OAuth2 flow params so /mfa-challenge can complete the code flow.
          arToken,
          tenantId,
        },
      });
      return;
    }

    const { access_token, refresh_token } = loginData as { access_token: string; refresh_token?: string };
    if (!access_token) {
      throw new Error('Server did not return an access token.');
    }

    // Handle Remember Me - store tokens with extended expiry
    if (rememberMe && refresh_token) {
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('remember_me', 'true');
      localStorage.setItem('token_expiry', String(Date.now() + 30 * 24 * 60 * 60 * 1000)); // 30 days
    }

    // Step 2: Complete the authorization code flow.
    // Go verifies the signed ar-token (ensures the authorize session hasn't expired
    // or been tampered with), verifies the user JWT, stores SHA-256(code) in DB,
    // and returns the full redirect URI with the one-time code embedded.
    const completeResp = await fetch(`${API_BASE_URL}/oauth2/authorize/complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant-ID': tenantId,
      },
      body: JSON.stringify({ ar: arToken, access_token }),
    });

    const completeData = await completeResp.json();

    if (!completeResp.ok) {
      const msg = completeData?.error_description ||
                  completeData?.message ||
                  'Authorization code exchange failed.';
      throw new Error(msg);
    }

    const { redirect_to, response_mode, form_params } = completeData as {
      redirect_to: string;
      response_mode?: string;
      form_params?: Record<string, string>;
    };

    // Handle form_post response mode: submit a hidden form via POST.
    if (response_mode === 'form_post' && form_params) {
      const { action, ...fields } = form_params;
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = action;
      Object.entries(fields).forEach(([k, v]) => {
        if (v !== undefined && v !== null) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = k;
          input.value = String(v);
          form.appendChild(input);
        }
      });
      document.body.appendChild(form);
      form.submit();
      return;
    }

    if (!redirect_to) {
      throw new Error('Server did not return a redirect URL.');
    }

    // Step 3: Redirect browser to client callback (cross-origin hop preserved here
    // because window.location.href is a full browser navigation, not a fetch).
    window.location.href = redirect_to;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.username || !form.password) {
      setError('Email and password are required');
      return;
    }
    setLoading(true);
    setError('');
    try {
      if (arToken) {
        // Go-native authorization_code flow â€” ar= token present (AUTHCODE_NATIVE=true).
        await submitToGo(form.username, form.password);
        // On success, submitToGo does window.location.href = redirect_to (page leaves).
      } else {
        // Legacy Spring form POST â€” AUTHCODE_NATIVE=false or non-auth_code flows.
        submitToSpring(form.username, form.password);
        // No finally â€” browser navigates away on success, or Spring redirects to /login?error.
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed. Please try again.');
      setLoading(false);
    }
  };



  const handleMagicLink = async () => {
    const email = form.username.trim();
    if (!email) {
      setError('Enter your email address first');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/auth/passwordless/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-ID': tenantId },
        body: JSON.stringify({ email, redirect_uri: window.location.origin + '/auth/callback' }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { message?: string; error_description?: string };
        throw new Error(body.error_description || body.message || `Magic link request failed (${resp.status})`);
      }
      setMagicLinkSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send magic link');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickLogin = async (persona: DevPersona) => {
    setLoading(true);
    setError('');
    try {
      if (arToken) {
        await submitToGo(persona.email, persona.password);
      } else {
        submitToSpring(persona.email, persona.password);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Quick login failed.');
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-header">
        <div className="auth-logo">{APP_NAME}</div>
        <div className="auth-subtitle">
          {arAppName ? `Sign in to ${arAppName}` : 'OIDC Conformance Client (zoauth.com)'}
        </div>
      </div>

      {/* Enterprise SSO buttons â€” rendered only when the tenant has configured IdPs */}
      {idps.length > 0 && (
        <div className="sso-idp-section">
          {idps.map(idp => (
            <button
              key={idp.id}
              type="button"
              className="sso-idp-btn"
              disabled={!!ssoLoading}
              onClick={() => handleSSOLogin(idp)}
            >
              {IDP_ICONS[idp.logo_hint] ? (
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                  <path d={IDP_ICONS[idp.logo_hint]} />
                </svg>
              ) : null}
              {ssoLoading === idp.id ? 'Redirectingâ€¦' : `Continue with ${idp.name}`}
            </button>
          ))}
          <div className="sso-divider">
            <span>or sign in with email</span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <TextInputModern
          name="username"
          label="Email Address"
          variant="email"
          value={form.username}
          onChange={(value) => setForm({ ...form, username: value })}
          placeholder="you@example.com"
          required
          size="lg"
          styleType="outline"
          labelType="stacked"
          error={error && !form.password ? error : undefined}
          autoComplete="username"
        />

        <TextInputModern
          name="password"
          label="Password"
          variant="password"
          value={form.password}
          onChange={(value) => setForm({ ...form, password: value })}
          placeholder="â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢"
          required
          size="lg"
          styleType="outline"
          labelType="stacked"
          passwordToggle
          error={error}
          autoComplete="current-password"
        />
        
        {error && attemptsRemaining !== null && (
          <Badge
            themeColor={attemptsRemaining === 0 ? 'danger' : 'warning'}
            variant="soft"
            size="small"
            style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Shield size={14} />
            {attemptsRemaining > 0 
              ? `${attemptsRemaining} ${attemptsRemaining === 1 ? 'attempt' : 'attempts'} remaining before 15-minute lockout`
              : 'Account temporarily locked. Try again in 15 minutes.'}
          </Badge>
        )}

        <Checkbox
          name="remember-me"
          label="Remember me for 30 days"
          checked={rememberMe}
          onChange={(e) => setRememberMe(e.target.checked)}
          color="primary"
          size="medium"
        />

        {/* reCAPTCHA v2 - Invisible mode */}
        <ReCAPTCHA
          ref={recaptchaRef}
          size="invisible"
          sitekey={RECAPTCHA_SITE_KEY}
          onChange={(token) => setCaptchaToken(token)}
        />

        <Button
          type="submit"
          variant="primary"
          size="large"
          disabled={loading}
          loading={loading}
          style={{ width: '100%', marginTop: '8px' }}
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </Button>
      </form>

      <div className="footer-links">
        <Link to="/forgot-password" className="footer-link">
          Forgot password?
        </Link>
        <span style={{ color: 'var(--text-secondary)' }}>Â·</span>
        <Link to="/register" className="footer-link">
          Create account
        </Link>        <span style={{ color: 'var(--text-secondary)' }}>·</span>
        <Link to="/signup" className="footer-link">
          Sign up organization
        </Link>      </div>

      {/* Magic link passwordless sign-in */}
      <div className="magic-link-section" style={{ marginTop: '16px', textAlign: 'center' }}>
        {magicLinkSent ? (
          <div className="magic-link-sent" style={{ padding: '12px', background: 'var(--success-bg, #f0fdf4)', borderRadius: '8px', color: 'var(--success-text, #166534)' }}>
            <strong>Check your inbox!</strong> We sent a sign-in link to <em>{form.username}</em>.
          </div>
        ) : (
          <>
            <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '8px' }}>
              Prefer not to use a password?
            </div>
            <button
              type="button"
              className="btn btn-outline"
              style={{ width: '100%' }}
              disabled={loading}
              onClick={handleMagicLink}
            >
              {loading ? 'Sendingâ€¦' : 'âœ‰ Send me a sign-in link'}
            </button>
          </>
        )}
      </div>

      {/* Development Mode: Quick Login */}
      {IS_DEVELOPMENT && (
        <div className="dev-login-section">
          <button
            type="button"
            className="dev-toggle"
            onClick={() => setDevOpen(!devOpen)}
          >
            <Zap size={16} />
            <span>Quick Login (Dev Mode)</span>
            <span className="dev-toggle-arrow" style={{ transform: devOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>â–¼</span>
          </button>

          {devOpen && (
            <div className="dev-personas">
              {DEV_PERSONAS.map((persona) => (
                <button
                  key={persona.email}
                  type="button"
                  className="dev-persona-btn"
                  onClick={() => handleQuickLogin(persona)}
                  disabled={loading}
                  style={{ borderLeftColor: persona.color }}
                >
                  <div className="dev-persona-header">
                    <span className="dev-persona-label" style={{ color: persona.color }}>
                      {persona.label}
                    </span>
                    <span className="dev-persona-email">{persona.email}</span>
                  </div>
                  <div className="dev-persona-desc">{persona.description}</div>
                </button>
              ))}
              <div className="dev-hint">
                ðŸ’¡ All demo users use password: <code>Admin123!@Pwd</code>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
